import { Delete, type SvgIconComponent } from '@mui/icons-material';
import {
  Checkbox,
  ListItemIcon,
  ListItemText,
  TableCell,
  type CheckboxProps,
  type MenuItemProps,
  type TableCellProps,
} from '@mui/material';
import * as React from 'react';
import { forwardRef } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconButton, type IconButtonProps } from '~/components/IconButton';
import { TooltipMenuItem, type TooltipMenuItemProps } from '~/components/menu-item';
import { IconButtonLink, MenuItemLink, type LinkProps } from './link';

export type TableActionProps<T> = Omit<T, 'label'> & {
  Icon: SvgIconComponent;
  title: string;
};

/**
 * Props a data grid actions cell clones onto each action: the cell runs a roving tab stop
 * over them, and the grids give each the menuitem role the cell's role=menu requires. A
 * component that wraps its button must pass them on to it (see `mergeGridActionProps`).
 */
export type GridActionCellProps = Pick<
  IconButtonProps,
  'onClick' | 'role' | 'tabIndex' | 'touchRippleRef'
>;

/**
 * Click handler for an action that opens a menu or popover from a data grid actions cell. The
 * cell refocuses the clicked button in an effect, so that runs first or it would pull focus
 * back out of what `open` just opened.
 */
export const gridActionOpener =
  (
    onClick: GridActionCellProps['onClick'],
    open: ((event: React.MouseEvent<HTMLButtonElement>) => void) | undefined,
  ) =>
  (event: React.MouseEvent<HTMLButtonElement>) => {
    flushSync(() => onClick?.(event));
    open?.(event);
  };

/** The cell's props merged over an action's own, its click handler running first. */
export const mergeGridActionProps = <
  P extends { onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void },
>(
  own: P,
  cell: GridActionCellProps,
): P & GridActionCellProps => ({
  ...own,
  ...cell,
  onClick: gridActionOpener(cell.onClick, own.onClick),
});

/**
 * The props a `TableAction` would hand its `IconButton`, for a menu that renders the button
 * itself: `Icon` and `title` are the action's own and would otherwise land on the DOM button.
 */
export const tableActionButtonProps = <P extends { Icon?: unknown; title?: unknown }>({
  Icon: _Icon,
  title: _title,
  ...props
}: P): Omit<P, 'Icon' | 'title'> => props;

export const TableAction = ({
  color = 'secondary',
  Icon,
  title,
  ...props
}: TableActionProps<IconButtonProps>) => {
  return (
    <IconButton color={color} label={title} {...props} size="large">
      <Icon fontSize="small" />
    </IconButton>
  );
};

export const TableActionLink = ({
  color = 'secondary',
  Icon,
  title,
  ...props
}: TableActionProps<LinkProps<IconButtonProps>>) => {
  return (
    <IconButtonLink color={color} label={title} {...props}>
      <Icon fontSize="small" />
    </IconButtonLink>
  );
};

export interface TableCheckboxProps extends TableCellProps {
  checkboxProps: CheckboxProps;
}

export const TableCheckbox = ({ checkboxProps, ...props }: TableCheckboxProps) => (
  <TableCell padding="checkbox" {...props}>
    <Checkbox {...checkboxProps} />
  </TableCell>
);

export interface MoreActionProps extends Omit<TooltipMenuItemProps, 'onClick' | 'title'> {
  closeMenu: () => void;
  Icon: SvgIconComponent;
  onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  title: string;
}

export const MoreAction = forwardRef<HTMLLIElement, MoreActionProps>(
  //
  function MoreAction({ closeMenu, Icon, onClick, title, ...props }, ref) {
    return (
      <TooltipMenuItem
        {...props}
        ref={ref}
        onClick={(event) => {
          closeMenu();
          onClick(event);
        }}
      >
        <ListItemIcon>
          <Icon />
        </ListItemIcon>
        <ListItemText>{title}</ListItemText>
      </TooltipMenuItem>
    );
  },
);

export const MoreActionLink = forwardRef<
  HTMLAnchorElement,
  TableActionProps<LinkProps<MenuItemProps<'a'>>>
>(function MoreActionLink({ Icon, title, ...props }, ref) {
  return (
    <MenuItemLink ref={ref} {...props}>
      <ListItemIcon>
        <Icon />
      </ListItemIcon>
      <ListItemText>{title}</ListItemText>
    </MenuItemLink>
  );
});

export interface MoreDeleteActionProps extends TooltipMenuItemProps {
  closeMenu: () => void;
  onClick: NonNullable<MenuItemProps['onClick']>;
}

export const MoreDeleteAction = forwardRef<HTMLLIElement, MoreDeleteActionProps>(
  function MoreDeleteAction({ closeMenu, onClick, ...props }, ref) {
    const { t } = useTranslation();

    return (
      <TooltipMenuItem
        onClick={(event) => {
          closeMenu();
          onClick(event);
        }}
        {...props}
        ref={ref}
      >
        <ListItemIcon>
          <Delete color="error" />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ color: 'error' }}>
          {t('common.actions.delete')}
        </ListItemText>
      </TooltipMenuItem>
    );
  },
);
